import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { ImageFolder } from "../../enum/imageCategoryEnums";
import { deleteFilesInDir, generateImagesArrJson, syncImages } from "../../services/firebase.service";
import { Pagination } from "../../types/Pagination";
import { Vehicle } from "../../types/Vehicle";
import { VehicleFilterType } from "../../types/filters/VehicleFilterType";
import { resetCacheStamp } from "../../util/cacheManager";
import { generateFilterString, generatePaginationString, generateKeywordsString, handleNetworkResponse } from "../../util/helper";
import { CacheStoreKey } from "../../enum/cacheStore.enum";
import { config } from "../../config";
import { Persister } from "../../util/persister";
import { PopupManager } from "../../util/popupManager";

// Define a service using a base URL and expected endpoints
export const vehicleApi = createApi({
  reducerPath: "vehicleApi",
  tagTypes: [CacheStoreKey.userCacheStampVehicles],
  baseQuery: fetchBaseQuery({ baseUrl: "" }),
  endpoints: (build) => ({
    // mutation
    createVehicle: build.mutation({
      queryFn: async (args: { veh: Vehicle; imgs: Record<number, Blob | null> }) => {
        resetCacheStamp(CacheStoreKey.userCacheStampVehicles);
        // @ts-expect-error
        delete args.veh.id;
        args.veh.images = generateImagesArrJson(args.veh.UserId, args.veh.images, args.imgs, ImageFolder.Vehicle, config.tempResourceId);

        const response = await axios.post(
          `/vehicle/create`,
          args.veh
        );
        if (response.data.status && args.imgs) {
          await syncImages(args.veh.UserId, ImageFolder.Vehicle, response.data.data.id, args.imgs);
        }
        return handleNetworkResponse(response, null);
      },
      invalidatesTags: [CacheStoreKey.userCacheStampVehicles],
    }),

    // mutation
    updateVehicle: build.mutation({
      queryFn: async (args: {
        veh: Vehicle;
        imgs?: Record<number, Blob | null>
      }) => {
        resetCacheStamp(CacheStoreKey.userCacheStampVehicles);
        if (args.imgs) {
          args.veh.images = generateImagesArrJson(args.veh.UserId, args.veh.images, args.imgs, ImageFolder.Vehicle, args.veh.id);
        }
        const response = await axios.post(`/vehicle/update`, args.veh);
        if (response.data.status && args.imgs) {
          await syncImages(args.veh.UserId, ImageFolder.Vehicle, response.data.data.id, args.imgs);
        }
        return handleNetworkResponse(response, null);
      },
      invalidatesTags: [CacheStoreKey.userCacheStampVehicles],
    }),

    // mutation
    deleteVehicle: build.mutation({
      queryFn: async (veh: Vehicle) => {
        resetCacheStamp(CacheStoreKey.userCacheStampVehicles);
        PopupManager.showInfoToast('Deleting images...');
        await deleteFilesInDir(veh.UserId, ImageFolder.Vehicle, veh.id);
        PopupManager.showInfoToast('Deleting Vehicle...');
        const response = await axios.post(`/vehicle/delete/${veh.id}`);
        return handleNetworkResponse(response, null);
      },
      invalidatesTags: [CacheStoreKey.userCacheStampVehicles],
    }),

    // read
    // this is used for load favorites
    getVehiclesWithIds: build.query({
      queryFn: async (args: { ids: number[] }) => {
        if (args.ids.length < 1) {
          return {
            data: null,
          };
        }
        // Favorite vehicles most probably belongs to other users. So fetch them with std cache
        const response = await Persister.requestData(CacheStoreKey.noCache, 'GET', `/vehicle/byIds?ids=${JSON.stringify(args.ids)}`);
        return handleNetworkResponse(response, []);
      },
      providesTags: [CacheStoreKey.userCacheStampVehicles],
    }),

    // read
    // for loading vehicle details page
    getVehicleById: build.query({
      queryFn: async (args: { id: number; ownVehicle: boolean }) => {
        if (args.id === -1 || isNaN(args.id)) {
          return {
            data: null,
          };
        }
        const response = await Persister.requestData(args.ownVehicle ? CacheStoreKey.userCacheStampVehicles : CacheStoreKey.noCache, 'GET', `/vehicle/${args.id}`);
        return handleNetworkResponse(response, null);
      },
      providesTags: [CacheStoreKey.userCacheStampVehicles],
    }),

    // read
    searchVehicles: build.query({
      queryFn: async (args: Pagination & { keywords: string, filter: Partial<VehicleFilterType> }) => {

        const keywordString = generateKeywordsString(args.keywords);
        const filterString = generateFilterString(args.filter);
        const paginationString = generatePaginationString(args.limit, args.offset);
        const response = await Persister.requestData(CacheStoreKey.noCache, 'GET', `/vehicle/search?${keywordString}&${filterString}${paginationString}`);

        return handleNetworkResponse(response, []);
      },
      providesTags: [CacheStoreKey.userCacheStampVehicles],
    }),

    // read
    // fetch vehicles of user
    readCurrentUserVehicles: build.query({
      queryFn: async (args: { UserId: number }) => {
        // Fetch vehicles listed for myOwn
        const response = await Persister.requestData(CacheStoreKey.userCacheStampVehicles, 'GET', `/vehicles/user/${args.UserId}`);
        return handleNetworkResponse(response, []);
      },
      providesTags: [CacheStoreKey.userCacheStampVehicles],
    }),

    // read
    // Fetch vehicles binded to this user
    // TODO: Implement cache update logic in BE to update cache stamp of user once the vehicles binded to this user are updated 
    readUsersVehicles: build.query({
      queryFn: async (args: { UserId: number }) => {
        const response = await Persister.requestData(CacheStoreKey.noCache, 'GET', `/vehicles/users/${args.UserId}`);
        return handleNetworkResponse(response, []);
      },
      providesTags: [CacheStoreKey.userCacheStampVehicles],
    }),

    // read: No need to cache admin actions
    getNewAdditionsToVehicle: build.query({
      queryFn: async (type: 'model' | 'brand' | 'area') => {
        const response = await axios.get(`/vehicle/newAdditions/${type}`);
        return handleNetworkResponse(response, []);
      },
    }),
  }),
});

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  useUpdateVehicleMutation,
  useGetVehicleByIdQuery,
  useCreateVehicleMutation,
  useDeleteVehicleMutation,
  useReadCurrentUserVehiclesQuery,
  useReadUsersVehiclesQuery,
  useGetNewAdditionsToVehicleQuery,
  useSearchVehiclesQuery,
  useLazyGetVehiclesWithIdsQuery
} = vehicleApi;

export type VehicleApiType = typeof vehicleApi;